// =============================================================================
// MopsGuideList — the MOPs library as the crew reads it on the phone
// (27 Sep 2026)
// =============================================================================
// James: "the /mops screen that has the client and carrier MOPs — all the
// different work instructions like how to install a Telstra cabinet or an
// NBN MW link or a LeBlanc monopole … they should be able to see the carrier
// MOPs as reference guides / library."
//
// Same rows as MopsLibraryScreen (document_library_lines in the 'mops'
// category; RLS reads for everyone signed in; the master file signs through
// the gateway, which allows any authenticated user on a master path). This
// is the read-only, one-hand version: search, a row of source chips (NBN /
// Telstra / Optus / LeBlanc / Coretel …), grouped under the source, tap to
// open. No upload, no replace, no move — that stays on /mops for the office.
//
// Opening a file on a phone: the tab must be opened SYNCHRONOUSLY in the tap
// (James, 8 Sep) — open a blank window first, steer it once the URL is back.
// =============================================================================
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, BookOpen, ExternalLink, FileType2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Spinner, EmptyState } from "@/components/ui";
import { getDeliverableSignedUrl } from "@/features/deliverables/deliverablesUpload";

interface GuideRow {
  line_id: string; carrier_id: string | null; supplier_id: string | null; is_other: boolean;
  code: string; display_name: string; description: string | null;
  has_master: boolean; master_storage_path: string | null; master_version_label: string | null;
  master_uploaded_at: string | null; master_file_name: string | null; master_file_size_bytes: number | null; master_mime_type: string | null;
  sort_order: number | null;
  carrier: { carrier_code: string; legal_name: string } | null;
  supplier: { legal_name: string; trading_name: string | null } | null;
}

function sourceOf(l: GuideRow): { key: string; label: string; kind: "carrier" | "supplier" | "internal" | "other" } {
  if (l.carrier_id) return { key: `carrier:${l.carrier_id}`, label: l.carrier?.carrier_code ?? "Carrier", kind: "carrier" };
  if (l.supplier_id) return { key: `supplier:${l.supplier_id}`, label: l.supplier?.trading_name ?? l.supplier?.legal_name ?? "Supplier", kind: "supplier" };
  if (l.is_other) return { key: "other", label: "Other", kind: "other" };
  return { key: "internal", label: "Coretel", kind: "internal" };
}

function fmtSize(n: number | null | undefined): string {
  if (!n) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function useMopsGuides() {
  const { data: categoryId } = useQuery({
    queryKey: ["mops_category_id"],
    queryFn: async () => {
      const { data, error } = await supabase.from("document_library_categories").select("category_id").eq("code", "mops").maybeSingle();
      if (error) throw error;
      return (data?.category_id as string | undefined) ?? null;
    },
    staleTime: 10 * 60 * 1000,
  });
  const q = useQuery({
    enabled: !!categoryId,
    queryKey: ["mops_lines", categoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_library_lines")
        .select(`line_id, carrier_id, supplier_id, is_other, code, display_name, description,
                 has_master, master_storage_path, master_version_label, master_uploaded_at,
                 master_file_name, master_file_size_bytes, master_mime_type, sort_order,
                 carrier:carriers(carrier_code, legal_name), supplier:suppliers(legal_name, trading_name)`)
        .eq("category_id", categoryId!).eq("is_active", true).order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as GuideRow[];
    },
  });
  return { lines: q.data ?? [], isLoading: q.isLoading || (categoryId === undefined) };
}

export function openGuide(line: GuideRow) {
  if (!line.master_storage_path) return;
  const win = window.open("", "_blank");
  if (win) win.opener = null;
  const inline = (line.master_mime_type ?? "") === "application/pdf" || (line.master_mime_type ?? "").startsWith("image/");
  getDeliverableSignedUrl(line.master_storage_path, `${inline ? "inline" : "attachment"}; filename="${line.master_file_name ?? line.display_name}"`)
    .then(url => {
      if (!url) { win?.close(); alert("Could not open that document."); return; }
      if (win) win.location.href = url; else window.location.href = url;
    });
}

export default function MopsGuideList({ title = "Carrier & supplier MOPs" }: { title?: string }) {
  const { lines, isLoading } = useMopsGuides();
  const [search, setSearch] = useState("");
  const [source, setSource] = useState<string>("all");

  const sources = useMemo(() => {
    const m = new Map<string, { key: string; label: string; kind: string; n: number }>();
    for (const l of lines) { const s = sourceOf(l); const e = m.get(s.key) ?? { ...s, n: 0 }; e.n++; m.set(s.key, e); }
    const order = { carrier: 0, supplier: 1, internal: 2, other: 3 } as Record<string, number>;
    return [...m.values()].sort((a, b) => (order[a.kind] - order[b.kind]) || a.label.localeCompare(b.label));
  }, [lines]);

  const q = search.trim().toLowerCase();
  const shown = lines.filter(l => (source === "all" || sourceOf(l).key === source)
    && (!q || `${l.display_name} ${l.code} ${l.description ?? ""} ${sourceOf(l).label}`.toLowerCase().includes(q)));
  const groups = new Map<string, { label: string; rows: GuideRow[] }>();
  for (const s of sources) groups.set(s.key, { label: s.label, rows: [] });
  for (const l of shown) groups.get(sourceOf(l).key)?.rows.push(l);

  if (isLoading) return <div className="py-10 flex justify-center"><Spinner size={18} /></div>;
  if (lines.length === 0) {
    return <Card><EmptyState title="No MOPs in the library yet" description="The office loads the carrier and supplier work instructions on the MOPs page; they show here for everyone." icon={BookOpen} /></Card>;
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono px-1">{title} · {lines.length}</div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search — cabinet, monopole, MW link…"
               className="w-full bg-ink-raised border border-line rounded-md pl-9 pr-3 py-2.5 text-[14px] text-text placeholder:text-text-dim focus:outline-none focus:border-accent" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
        {[{ key: "all", label: "All", n: lines.length }, ...sources].map(s => (
          <button key={s.key} type="button" onClick={() => setSource(s.key)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-medium whitespace-nowrap ${source === s.key ? "bg-accent/15 text-accent border-accent/40" : "border-line text-text-muted"}`}>
            {s.label} <span className="opacity-60">{s.n}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 && <div className="text-[13px] text-text-muted py-6 text-center">Nothing matches "{search}".</div>}
      {[...groups.values()].filter(g => g.rows.length > 0).map(g => (
        <div key={g.label}>
          <div className="flex items-center justify-between mb-1.5 px-1">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">{g.label}</div>
            <div className="text-[11px] text-text-dim">{g.rows.length}</div>
          </div>
          <Card>
            <div className="divide-y divide-line">
              {g.rows.map(l => {
                const openable = l.has_master && !!l.master_storage_path;
                return (
                  <button key={l.line_id} type="button" disabled={!openable} onClick={() => openGuide(l)}
                          className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-ink-raised disabled:opacity-60">
                    <div className="w-11 h-11 rounded-md border border-line bg-ink-raised flex items-center justify-center flex-shrink-0 text-text-muted"><FileType2 size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-text leading-snug line-clamp-2 break-words">{l.display_name}</div>
                      <div className="text-[11px] text-text-muted mt-0.5 truncate">
                        <span className="font-mono">{l.code}</span>
                        {l.master_version_label && ` · ${/^v/i.test(l.master_version_label) ? "" : "v"}${l.master_version_label}`}
                        {l.master_file_size_bytes ? ` · ${fmtSize(l.master_file_size_bytes)}` : ""}
                        {!openable && " · no file on record yet"}
                      </div>
                      {l.description && <div className="text-[11px] text-text-dim mt-0.5 line-clamp-2">{l.description}</div>}
                    </div>
                    {openable && <ExternalLink size={14} className="text-text-muted flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
}
